import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { MenuItem, styled, useMediaQuery } from '@material-ui/core';
import theme from '../../../theme';
import { Container } from '@material-ui/core';
import { Button, TextButton } from '../../../components/spoon/Button';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '../../../components/core/Dialog';
import { DateCalendar } from '@cof/omni-react';
import { GuestSelector } from '../../../components/core/Select';
import {
  PARTY_SIZE_DROPDOWN_LABEL,
  REFINE_SEARCH_TEXT,
  REFINE_SEARCH_UPDATE_LABEL,
  REFINE_SEARCH_CLEAR_LABEL,
  SELECT_TIME_INPUT_LABEL,
  SELECT_PARTY_SIZE_INPUT_LABEL,
} from '../../../assets/copy';
import TimeSelector from '../../../components/core/Select/TimeSelector';
import clsx from 'clsx';
import {
  actionClearAvailability,
  actionResetRefinedSearchValues,
  actionSetRefinedSearchValues,
} from '../../../store/Availability/AvailabilityAction';
import {
  trackClickWithDescription,
  trackSiteSearch,
} from '../../../utils/useOneTag';

const StyledMenuItem = styled(MenuItem)({
  borderBottom: '1px solid #C9CED2',
  minHeight: 48,
  '& .MuiTypography-colorTextPrimary': {
    color: '#011728'
  }
});

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: '16px',
  },
  calendarContainer: {
    display: 'inline-block',
    position: 'relative',
    fontFamily: theme.typography.medium1Normal.fontFamily,
    '& th[scope="col"]': {
      fontWeight: '400',
    },
    '& span[data-omni-part="day"]': {
      marginBottom: 0,
    },
  },
  hideNextButton: {
    '& button[aria-label="Next month"]': {
      visibility: 'hidden',
      pointerEvents: 'none',
    },
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    alignItems: 'center',
    paddingBottom: theme.spacing(8.5),
    overflowX: 'hidden',
  },
  ctaButton: {
    fontSize: 16,
    lineHeight: '24px',
    padding: theme.spacing(1.5),
    width: '100%',
  },
  hidden: {
    visibility: 'hidden',
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 0,
      paddingTop: theme.spacing(6),
    },
  },
  dialogHeader: {
    height: theme.spacing(7),
  },
  dialogTitle: {
    fontSize: 16,
    lineHeight: '24px',
    textAlign: 'center',
    position: 'relative',
    top: theme.spacing(-4.75),
    marginBottom: 0,
    pointerEvents: 'none',
  },
}));

export const RefineSearch = ({
  open,
  onClose,
  onUpdate,
  guestSelectionLabel = PARTY_SIZE_DROPDOWN_LABEL,
  showTodayLabel = true,
  ...rest
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { refinedDate, refinedTime, refinedGuests } = useSelector(
    (state) => state.availability
  );

  const [selectedDate, setSelectedDate] = useState(refinedDate);
  const [selectedTime, setSelectedTime] = useState(refinedTime);
  const [selectedGuests, setSelectedGuests] = useState(refinedGuests);

  const handleSelectDate = (date) => {
    if (date !== selectedDate) {
      trackClickWithDescription(
        'search-revamp-refine-search-select-date',
        'Button',
        'select ' + date
      );
      setSelectedDate(date);
    }
  };

  const handleSelectTime = (e) => {
    if (e.target.value !== selectedTime) {
      trackClickWithDescription(
        'search-revamp-refine-search-select-time',
        'Button',
        'select ' + e.target.value
      );
      setSelectedTime(e.target.value);
    }
  };

  const handleSelectGuests = (e) => {
    if (e.target.value !== selectedGuests) {
      trackClickWithDescription(
        'search-revamp-refine-search-select-guests',
        'Button',
        'select ' + e.target.value
      );
      setSelectedGuests(e.target.value);
    }
  };

  const handleClose = () => {
    trackClickWithDescription(
      'search-revamp-refine-search-close',
      'Button',
      ''
    );
    if (
      refinedDate &&
      refinedTime &&
      refinedGuests &&
      !selectedDate &&
      !selectedTime &&
      !selectedGuests
    ) {
      dispatch(actionResetRefinedSearchValues());
      dispatch(actionClearAvailability());
    } else {
      setSelectedDate(refinedDate);
      setSelectedTime(refinedTime);
      setSelectedGuests(refinedGuests);
    }
    onClose();
  };

  const handleUpdate = () => {
    trackClickWithDescription(
      'search-revamp-refine-search-update',
      'Button',
      ''
    );
    trackSiteSearch(
      '',
      '',
      JSON.stringify({
        date: selectedDate,
        time: selectedTime,
        guests: selectedGuests,
      })
    );
    dispatch(
      actionSetRefinedSearchValues({
        refinedDate: selectedDate,
        refinedTime: selectedTime,
        refinedGuests: selectedGuests,
      })
    );
    onUpdate({
      selectedDate,
      selectedTime,
      selectedGuests,
    });
    onClose();
  };

  const onClear = () => {
    trackClickWithDescription(
      'search-revamp-refine-search-clear',
      'Button',
      ''
    );
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedGuests(null);
    dispatch(actionResetRefinedSearchValues());
    onUpdate({
      selectedDate: null,
      selectedTime: null,
      selectedGuests: null,
    });
    onClose();
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(2026, 11, 31);

  function isDateDisabled(date) {
    return date < today;
  }

  return (
    <Dialog
      title={REFINE_SEARCH_TEXT}
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      classes={{
        dialog: classes.dialog,
        header: classes.dialogHeader,
        title: classes.dialogTitle,
      }}>
      <div className={classes.root}>
        <Container className={classes.contentContainer}>
          <div className={classes.calendarContainer}>
            <DateCalendar
              {...rest}
              date={selectedDate ? new Date(selectedDate) : null}
              minDate={today}
              maxDate={maxDate}
              onDateSelected={handleSelectDate}
              variant={'inline'}
              showTodayLabel={showTodayLabel}
              isDateDisabled={(date) => isDateDisabled(date)}
            />
          </div>
          <TimeSelector
            value={selectedTime ?? ''}
            onChange={handleSelectTime}
            placeholder={SELECT_TIME_INPUT_LABEL}
            native={false}
            OptionComponent={StyledMenuItem}
          />
          <GuestSelector
            minGuests={1}
            maxGuests={10}
            value={selectedGuests ?? ''}
            dropdownLabel={guestSelectionLabel}
            formatGuestValue={true}
            onChange={handleSelectGuests}
            placeholder={SELECT_PARTY_SIZE_INPUT_LABEL}
            native={false}
            OptionComponent={StyledMenuItem}
          />
          <Button
            classes={{ root: classes.ctaButton }}
            onClick={() => {
              handleUpdate();
            }}
            disabled={!(selectedDate && selectedTime && selectedGuests)}>
            {REFINE_SEARCH_UPDATE_LABEL}
          </Button>
          <TextButton
            classes={{
              root: clsx(
                classes.ctaButton,
                !(selectedDate && selectedTime && selectedGuests) &&
                classes.hidden
              ),
            }}
            onClick={() => {
              onClear();
            }}>
            {REFINE_SEARCH_CLEAR_LABEL}
          </TextButton>
        </Container>
      </div>
    </Dialog>
  );
};

export default RefineSearch;

RefineSearch.propTypes = {
  guestSelectionLabel: PropTypes.string,
  showTodayLabel: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
